import type { HTMLAttributes } from 'react';
import { Button } from '../Button/Button';
import { Icon } from '../Icon';
import { Tooltip } from '../Tooltip';
import styles from './StatusField.module.css';

export type StatusType = 
  | 'aprobado'
  | 'activa'
  | 'bloqueada'
  | 'sin-limite-asignado'
  | 'solicitada'
  | 'recibida'
  | 'pendiente'
  | 'rechazado'
  | 'settled'
  | 'approved'
  | 'unsettled'
  | 'reversed'
  | 'failed'
  | 'on_transit'
  | 'declined'
  | 'ordered'
  | 'confirm'
  | 'customazing'
  | 'sent'
  | 'received'
  | 'invited'
  | 'in_progress'
  | 'blocked'
  | 'disabled'
  | 'enabled'
  | 'pending_approval'
  | 'rejected';


export type StatusColor = 'success' | 'info' | 'error' | 'warning' | 'default' | 'muted';

export interface StatusFieldProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * Texto del estatus a mostrar
   */
  status: string;
  /**
   * Tipo de estatus para mapear el color
   */
  statusType?: StatusType;
  /**
   * Color del estatus (sobrescribe statusType si se proporciona)
   */
  statusColor?: StatusColor;
  /**
   * Label del botón de acción (si no se proporciona, no se muestra el botón)
   */
  actionLabel?: string;
  /**
   * Callback cuando se hace click en el botón de acción
   */
  onActionClick?: () => void;
  /**
   * Si se muestra el icono de información para reportar
   */
  showReportIcon?: boolean;
  /**
   * Tooltip o texto adicional para el icono de reporte
   */
  reportTooltip?: string;
}

const statusTypeToColor: Record<StatusType, StatusColor> = {
  'aprobado': 'success',
  'activa': 'success',
  'recibida': 'success',
  'bloqueada': 'error',
  'rechazado': 'error',
  'sin-limite-asignado': 'warning',
  'solicitada': 'info',
  'pendiente': 'warning',
  // CARDS
  'settled': 'success',
  'approved': 'success',
  'confirm': 'success',
  'sent': 'success',
  'received': 'success',
  'on_transit': 'info',
  'ordered': 'info',
  'reversed': 'info',
  'unsettled': 'warning',
  'customazing': 'warning',
  'failed': 'error',
  'declined': 'error',
  // TEAM
  'invited': 'muted',
  'in_progress': 'warning',
  'enabled': 'success',
  'pending_approval': 'warning',
  'blocked': 'error',
  'disabled': 'error',
  'rejected': 'error',
};

export function StatusField({
  status,
  statusType,
  statusColor,
  actionLabel,
  onActionClick,
  showReportIcon = false,
  reportTooltip,
  className,
  ...props
}: StatusFieldProps) {
  // Determinar el color del status
  const color = statusColor || (statusType ? statusTypeToColor[statusType] : 'default');

  const classes = [
    styles.statusField,
    styles[color],
    className
  ].filter(Boolean).join(' ');

  return (
    <div className={classes} {...props}>
      <span className={styles.statusText}>{status}</span>
      {(actionLabel || showReportIcon) && (
        <div className={styles.actions}>
          {actionLabel && (
            <Button
              variant="link-secondary"
              size="sm"
              onClick={onActionClick}
              className={styles.actionButton}
            >
              {actionLabel}
            </Button>
          )}
          {showReportIcon && (
            reportTooltip ? (
              <Tooltip content={reportTooltip} position="left">
                <Icon
                  name="information"
                  size="sm"
                  color="secondary"
                  className={styles.reportIcon}
                  aria-label={reportTooltip || 'Información para reportar'}
                />
              </Tooltip>
            ) : (
              <Icon
                name="information"
                size="sm"
                color="secondary"
                className={styles.reportIcon}
                aria-label="Información para reportar"
              />
            )
          )}
        </div>
      )}
    </div>
  );
}

StatusField.displayName = 'StatusField';
